import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@noor/api';
import { toast } from '@noor/lib';
import {
  conversationsApi,
  conversationsKeys,
  type Chat,
  type ConversationId,
} from '@/entities/conversation';
import { usePendingOfferStore } from './pending-offer-store';

/**
 * Chat write-actions (cpanel `ChatMessageInput` / `ChatRoom` / `useChatPage`
 * mutations). Each invalidates through `conversationsKeys`; the outcome of
 * every one is server-decided (acquire assigns an operator, closing moves the
 * status, …), so none is optimistic — the refetch is the source of truth.
 * Failures toast a translated message (the backend's own, when it sends one).
 */

function useInvalidateChat() {
  const queryClient = useQueryClient();
  return (chatId?: ConversationId) => {
    void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
    if (chatId != null) {
      void queryClient.invalidateQueries({ queryKey: conversationsKeys.detail(chatId) });
      void queryClient.invalidateQueries({ queryKey: conversationsKeys.thread(chatId) });
    }
  };
}

/** `POST chats/{id}/messages` — send text and/or one image. */
export function useSendMessage(chatId: ConversationId) {
  const { t } = useTranslation('inbox');
  const invalidate = useInvalidateChat();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (body: { message: string; files: File[] }) =>
      conversationsApi.sendMessage(chatId, body),
    onSuccess: () => invalidate(chatId),
    onError: (error) => toast.error(getApiErrorMessage(error, t('errors.send'))),
  });
}

/** `POST chats/{id}/acquire` — become the chat's responsible operator. */
export function useAcquireChat(chatId: ConversationId) {
  const { t } = useTranslation('inbox');
  const invalidate = useInvalidateChat();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: () => conversationsApi.acquire(chatId),
    onSuccess: () => invalidate(chatId),
    onError: (error) => toast.error(getApiErrorMessage(error, t('errors.acquire'))),
  });
}

/**
 * `POST chats/{id}/seen` — mark the open thread read. Silent, like cpanel: no
 * toast on failure, no cache invalidation (the other side learns of it through
 * the `messages_has_been_seen` push).
 */
export function useMarkChatSeen() {
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (chatId: ConversationId) => conversationsApi.markSeen(chatId),
  });
}

/** `PATCH chats/{id}` — close as resolved (3) / not resolved (4). */
export function useCloseChat(chatId: ConversationId) {
  const { t } = useTranslation('inbox');
  const invalidate = useInvalidateChat();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (status: number) => conversationsApi.setStatus(chatId, status),
    onSuccess: () => {
      toast.success(t('close.success'));
      invalidate(chatId);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('errors.generic'))),
  });
}

/** `PATCH chats/{id}/move` — hand the chat to another operator. */
export function useSwitchOperator(chatId: ConversationId) {
  const { t } = useTranslation('inbox');
  const invalidate = useInvalidateChat();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (userId: number | string) => conversationsApi.moveTo(chatId, userId),
    onSuccess: () => invalidate(chatId),
    onError: (error) => toast.error(getApiErrorMessage(error, t('errors.generic'))),
  });
}

/**
 * `POST chats/{id}/confirm-move` — accept (`true`) or reject (`false`) the
 * pending hand-over offer. Clears the offer once the server has answered.
 */
export function useConfirmMove() {
  const { t } = useTranslation('inbox');
  const invalidate = useInvalidateChat();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: ({ chat, confirm }: { chat: Chat; confirm: boolean }) =>
      conversationsApi.confirmMove(chat.id, confirm),
    onSuccess: (_data, { chat }) => {
      invalidate(chat.id);
      usePendingOfferStore.getState().setOffer(null);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('errors.generic'))),
  });
}
